/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: underscore library.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { MdAlternateEmail } from 'react-icons/md' // Npm: React icons.
import { HiArrowSmallRight, HiCheckCircle, HiCurrencyEuro, HiDocumentText, HiTrash } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  FormControl,
  Spinner,
  Text,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DateRangePicker from 'components/DateRangePicker'
import DownloadToExcel from 'components/DownloadToExcel'
import CustomerSelector from 'components/CustomerSelector'
import EntitySelector from 'components/EntitySelector'
import { MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import CustomerInvoiceCreateMutation from './__mutation__/index.customerInvoice.create.mutation'
import CustomerInvoiceDeleteMutation from './__mutation__/index.customerInvoice.delete.mutation'
import CustomerInvoiceEmailMutation from './__mutation__/index.customerInvoice.email.mutation'
import CustomerInvoiceReadQuery from './__query__/index.customerInvoice.read.query'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    'selection': {
      'startDate': Object.DateRangeProvider()?.startDate,
      'endDate': Object.DateRangeProvider()?.endDate,
      'key': 'selection'
    }
  })
  const [customerType, setCustomerType] = React.useState(void 0)
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [customerId, setCustomerId] = React.useState('')
  const [entityId, setEntityId] = React.useState('')
  const [invoiceEmailInProgress, setInvoiceEmailInProgress] = React.useState([])
  const [invoiceDeleteInProgress, setInvoiceDeleteInProgress] = React.useState([])
  const [error, setError] = React.useState('')
  const [MutationCustomerInvoiceCreate, MutationCustomerInvoiceCreateResponse] = useMutation(CustomerInvoiceCreateMutation)
  const [MutationCustomerInvoiceDelete] = useMutation(CustomerInvoiceDeleteMutation)
  const [MutationCustomerInvoiceEmail] = useMutation(CustomerInvoiceEmailMutation)
  const _tableHeaderHeightRef = React.useRef(0)
  const _QueryCustomerInvoiceRead = useQuery(CustomerInvoiceReadQuery, {
    'variables': {
      customerId,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Object assignment.
  const _DownloadCustomerInvoice = __url => {
    // Const assignment.
    const a = document.createElement('a')

    // Object assignment.
    a.href = __url
    a.download = __url.split('/').pop()

    /*
     * Append and remove child.
     * with fake click so that file can be downloaded.
     */
    document.body.appendChild(a)

    // Fake click.
    a.click()

    // Remove child.
    document.body.removeChild(a)
  }
  const _GenerateCustomerInvoice = async () => {
    // // Const assignment.
    // const _JoiSchema = JoiBrowser.object({
    //   'customerId': JoiBrowser.string().optional(),
    //   'entityId': JoiBrowser.string().optional()
    // })

    // // Validate form data.
    // const _JoiSchemaValidate = _JoiSchema.validate({ customerId, entityId })

    // // If error exists then report failure.
    // if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // Execute update mutation.
    const _MutationCustomerInvoiceCreate = await MutationCustomerInvoiceCreate({
      'variables': { customerId, entityId, 'smsStartDate': dateRange?.selection?.startDate, 'smsEndDate': dateRange?.selection?.endDate, customerType }
    })

    // If mutation caught an exception then report failure.
    if (_MutationCustomerInvoiceCreate instanceof Error) return _MutationCustomerInvoiceCreate

    // Style Guide.
    toast(_MutationCustomerInvoiceCreate?.data?.CustomerInvoiceCreate?.message)

    // Refetch customerInvoice updates.
    return _QueryCustomerInvoiceRead.refetch()
  }
  const _DeleteCustomerInvoice = async __id => {
    // Const assignment.
    const _JoiSchema = JoiBrowser.object({ 'id': JoiBrowser.string().required() })

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate({ 'id': __id })

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // Update state.
    setInvoiceDeleteInProgress([...invoiceDeleteInProgress, __id])

    // Execute update mutation.
    const _MutationCustomerInvoiceDelete = await MutationCustomerInvoiceDelete({ 'variables': { 'customerInvoiceId': __id } })

    // Update state.
    setInvoiceDeleteInProgress(invoiceDeleteInProgress.filter(j => j !== __id))

    // If mutation caught an exception then report failure.
    if (_MutationCustomerInvoiceDelete instanceof Error) return _MutationCustomerInvoiceDelete

    // Style Guide.
    toast(_MutationCustomerInvoiceDelete?.data?.CustomerInvoiceDelete?.message)

    // Refetch customerInvoice updates.
    return _QueryCustomerInvoiceRead.refetch()
  }
  const _EmailCustomerInvoice = async __id => {
    // Const assignment.
    const _JoiSchema = JoiBrowser.object({ 'id': JoiBrowser.string().required() })

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate({ 'id': __id })

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // Update state.
    setInvoiceEmailInProgress([...invoiceEmailInProgress, __id])

    // Execute update mutation.
    const _MutationCustomerInvoiceEmail = await MutationCustomerInvoiceEmail({ 'variables': { 'customerInvoiceId': __id } })

    // Update state.
    setInvoiceEmailInProgress(invoiceEmailInProgress.filter(j => j !== __id))

    // If mutation caught an exception then report failure.
    if (_MutationCustomerInvoiceEmail instanceof Error) return _MutationCustomerInvoiceEmail

    // Style Guide.
    toast(_MutationCustomerInvoiceEmail?.data?.CustomerInvoiceEmail?.message)

    // Refetch customerInvoice updates.
    return _QueryCustomerInvoiceRead.refetch()
  }

  // Event handler.
  React.useEffect(() => {
    // Clear error.
    setError('')
  }, [customerId, entityId])

  // Data assignment.
  const _isFirstLoadCompleted = React.useRef(false)
  if (!_QueryCustomerInvoiceRead.loading && 0 < _QueryCustomerInvoiceRead.data?.CustomerInvoiceRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryCustomerInvoiceRead?.data?.CustomerInvoiceRead?.length || (0 < _QueryCustomerInvoiceRead?.data?.CustomerInvoiceRead?.length && !_.every(_.pluck(_QueryCustomerInvoiceRead?.data?.CustomerInvoiceRead, 'status'), j => _successFlags.includes(j)))) _isFirstLoadCompleted.current = false

  // Columns definition.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No',
      'description': 'Serial number.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': true,
      'filterable': false
    },
    {
      'field': 'id',
      'headerName': 'Invoice Number',
      'description': 'Invoice number.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'sortable': true,
      'filterable': false
    },
    {
      'field': 'customer',
      'headerName': 'Customer',
      'description': 'Customer name.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 180,
      'sortable': true,
      'filterable': true
    },
    {
      'field': 'entityName',
      'headerName': 'Entity Name',
      'description': 'Entity name.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'sortable': true,
      'filterable': true
    },
    {
      'field': 'billingStartDate',
      'headerName': 'Billing Start Date',
      'description': 'Billing start date.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'sortable': true,
      'filterable': false
    },
    {
      'field': 'billingEndDate',
      'headerName': 'Billing End Date',
      'description': 'Billing end date.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'sortable': true,
      'filterable': false
    },
    {
      'field': 'amount',
      'headerName': 'Amount',
      'description': 'Invoice amount.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 120,
      'sortable': true,
      'filterable': false
    },
    {
      'field': 'trafficDetails',
      'headerName': 'Traffic Details',
      'description': 'Download traffic details.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'sortable': false,
      'filterable': false,
      'renderCell': ({ row }) => (
        <DownloadToExcel
          onlyShowIcon={true}
          bg='transparent'
          color='gray.500'
          _hover={{ 'bg': 'none' }}
          _active={{ 'bg': 'none' }}
          cellsData={_.isEmpty(row.detailedInvoice) ? [] : _.compact(_.flatten(Object.values(row.detailedInvoice)?.map(j => Object.values(j)?.map(r => Object.values(r)))))
            .filter(__d =>
              // Filter out rows with undefined or null values in critical fields.
              __d?.deliveryDate && 0 < __d?.totalSmsCount && __d?.mnc)
            .sort((a, b) => {
              // First sort by date.
              const dateCompare = new Date(a.deliveryDate) - new Date(b.deliveryDate)
              if (0 !== dateCompare) return dateCompare

              // If dates are same, sort by totalSmsCount.
              return Number(b.totalSmsCount) - Number(a.totalSmsCount)
            })
            .map(__d => ({
              'Period': __d?.deliveryDate?.split('T')[0] || '-',
              'Account Name': __d?.customerAccount ?? '-',
              'Mcc': __d?.mcc ?? '-',
              'Mnc': __d?.mnc,
              'Sms Count': __d?.totalSmsCount ?? '-',
              'Unit Cost': __d?.rate ?? '-',
              'Total Cost': __d?.totalCost ?? '-',
              'Currency': __d?.currency ?? '-'
            }))}
          headersData={[
            'Period',
            'Account Name',
            'Mcc',
            'Mnc',
            'Sms Count',
            'Unit Cost',
            'Total Cost',
            'Currency'
          ].map(i => ({ 'key': i, 'label': i }))}
        />
      )
    },
    {
      'field': 'invoice',
      'headerName': 'Invoice',
      'description': 'Download invoice PDF.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'sortable': true,
      'filterable': false,
      'renderCell': ({ row }) => (
        <Button
          color='gray.500'
          bg='none'
          fontWeight={600}
          fontSize='sm'
          gap='6px'
          _hover={{ 'bg': 'none', 'color': 'gray.600' }}
          _active={{ 'bg': 'none', 'color': 'gray.600' }}
          onClick={() => _DownloadCustomerInvoice(row.invoiceStoredAt?.path)}>
          <HiDocumentText size={15} />
          Download PDF
        </Button>
      )
    },
    {
      'field': 'email',
      'headerName': 'Email',
      'description': 'Email invoice to customer.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'sortable': false,
      'filterable': false,
      'renderCell': ({ row }) => {
        // Check if current invoice is in progress for email.
        const isEmailInProgress = invoiceEmailInProgress?.includes(row.originalId)

        return (
          <Button
            color='gray.500'
            bg='none'
            fontWeight={600}
            fontSize='sm'
            gap='6px'
            _hover={{ 'bg': 'none', 'color': 'gray.600' }}
            _active={{ 'bg': 'none', 'color': 'gray.600' }}
            onClick={() => _EmailCustomerInvoice(row.originalId)}>
            {isEmailInProgress ? <Spinner size='sm' color='brand.500' /> : <MdAlternateEmail size={15} />}
            Email Invoice
          </Button>
        )
      }
    },
    {
      'field': 'delete',
      'headerName': 'Delete',
      'description': 'Delete invoice.',
      'editable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'sortable': false,
      'filterable': false,
      'renderCell': ({ row }) => {
        return (
          <Button
            color='gray.500'
            bg='none'
            fontWeight={600}
            gap='6px'
            fontSize='sm'
            _hover={{ 'bg': 'none', 'color': 'red.500' }}
            _active={{ 'bg': 'none', 'color': 'red.500' }}
            onClick={() => _DeleteCustomerInvoice(row.originalId)}>
            {invoiceDeleteInProgress?.includes(row.originalId) ? <Spinner size='sm' color='red.500' /> : <HiTrash size={15} />}
            Delete Invoice
          </Button>
        )
      }
    }
  ], [invoiceDeleteInProgress, invoiceEmailInProgress])
  // Rows data.
  const rows = React.useMemo(() => _.compact(_QueryCustomerInvoiceRead?.data?.CustomerInvoiceRead?.map((item, index) =>
    _successFlags.includes(item.status) ? ({
      'id': item?.id || `invoice-${index}-${Date.now()}`,
      'originalId': item?.id,
      'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
      'customer': item?.Customer?.Company?.displayName || '-',
      'entityName': item?.Entity?.displayName || '-',
      'billingStartDate': Moment(item?.billingStartDate).local().format('YYYY-MM-DD'),
      'billingEndDate': Moment(item?.billingEndDate).local().format('YYYY-MM-DD'),
      'amount': item?.amount || '-',
      'detailedInvoice': item?.detailedInvoice,
      'invoiceStoredAt': item?.invoiceStoredAt
    }) : null
  )), [_QueryCustomerInvoiceRead?.data?.CustomerInvoiceRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex
        display='flex'
        flex={1}
        flexDirection='column'
        gap={_isCurrentViewMobile ? '12px' : '22px'}
        bg='white'
        borderRadius='20px'
        minH={_isCurrentViewMobile ? '128vh' : '98vh'}
        maxH={_isCurrentViewMobile ? 'auto' : '98vh'}
        p={_isCurrentViewMobile ? '12px' : '22px'}>
        <Flex
          w='100%'
          flexDir={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'stretch', md: 'end' }}
          gap='16px'
        >
          <Flex
            w='100%'
            flexDir={{ 'base': 'column', 'md': 'row' }}
            justifyContent='space-between'
            gap='12px'
            alignItems='center'>
            <Flex flex={{ base: 1, md: 0.6 }} flexDir='column' gap='22px'>
              <Flex flexDir='column' gap='12px'>
                <Flex color='brand.500' gap='6px' alignItems='center'>
                  <HiCurrencyEuro size='31px' />
                  <Text fontSize={31} fontWeight={300}>
                    Customer Invoice
                  </Text>
                </Flex>
                <Text w={{ 'base': '100%', 'md': '50%' }} fontSize={18} color='gray.500'>
                  Generate invoices for your customer based on time frame and
                  entity of your preference.
                </Text>
                <Text as='h3' fontSize={17} fontWeight={500} display='flex' alignItems='center' color='brand.500' gap='6px'>Select Billing date and other options.<HiArrowSmallRight /></Text>
              </Flex>
            </Flex>
            <Flex
              flex={{ base: 1, md: 0.4 }}
              zIndex={10}
              flexDir={{ base: 'column', md: 'row' }}
              gap='12px'
              justifyContent='flex-end'
            >

              <Flex flexDir='column' gap='12px'>
                <Flex flex={0.6} flexDir='row' gap='12px' justify='end' align='end'>
                  <Flex overflow='hidden' flexDir='column' w={{ base: '100%', md: '50%' }} gap='12px'>
                    <DateRangePicker asFatButton startDate={dateRange?.selection?.startDate} endDate={dateRange?.selection?.endDate} fontSize='13px' height='max-content' gap='6px' setSelectDate={setDateRange} />
                  </Flex>
                  <Flex w={{ base: '100%', md: '50%' }}>
                    <FormControl>
                      <EntitySelector
                        placeholder='Select Entity'
                        isInvalid={error?.includes?.('entityId')}
                        onChange={j => setEntityId(j.target.value.split?.('(')?.[1]?.split?.(')')?.[0])}
                      />
                    </FormControl>
                  </Flex>
                </Flex>
                <Flex mt='0' width='100%' gap='12px' flexDir={{ base: 'column', md: 'row' }}>
                  <Flex mt='0' width='100%' flex={1}>
                    <MemoizedSelect
                      placeholder='Select Account Type'
                      className='filter searchInput'
                      onChange={e => setCustomerType(_.isEmpty(e?.target?.value) ? void 0 : e?.target?.value)}
                      options={_.without(Object.React.App.enums.CUSTOMER_TYPE.enums?.map(i => i?.key), 'TERMINATION')}
                      data={customerType}
                    />
                  </Flex>
                  <Flex width='100%' flex={1}>
                    <CustomerSelector
                      customerType={customerType}
                      placeholder='Select Customer'
                      isInvalid={error?.includes?.('customerId')}
                      onChange={j => setCustomerId(j.target.value?.split?.('(')?.[1]?.split?.(')')?.[0])}
                    />
                  </Flex>
                </Flex>
              </Flex>
              <Flex>
                <Button
                  w={{ base: '100%', md: 'max-content' }}
                  bg='brand.500'
                  h={{ base: '64px', md: '100%' }}
                  flexDir='column-reverse'
                  color='gray.50'
                  borderRadius={22}
                  boxShadow='0px 4px 4px var(--chakra-colors-brand-100)'
                  _hover={{ 'bg': 'brand.600' }}
                  _active={{ 'bg': 'brand.600' }}
                  onClick={_GenerateCustomerInvoice}>
                  <Text fontWeight={500} fontSize='md' mt={2}>
                    Create Invoice
                  </Text>
                  {MutationCustomerInvoiceCreateResponse.loading ? (
                    <Spinner size='sm' color='#c1a9f4' />
                  ) : (
                    <HiCheckCircle size={26} />
                  )}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* MUI DataGrid */}
        <DataGrid
          key={`${paginationModel.page}-${paginationModel.pageSize}`}
          rows={rows}
          columns={columns}
          rowHeight={58}
          rowCount={_.pluck(_QueryCustomerInvoiceRead?.data?.CustomerInvoiceRead, '_totalCount')?.[0] || 0}
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 30, 50, 100]}
          slots={{
            'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
            'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
            'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
          }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
          loading={_QueryCustomerInvoiceRead?.loading}

        />
      </Flex>
    </>
  )
}
Index.propTypes = {}


/*
 * EXPORTS
 */
export default Index
